/**
 * The Rail's own state (fix D / R1): which BODY the rail state implies, whether
 * this is a casual game or a tournament one, and the two side effects that have
 * to happen where the rail can see both the tabs and the analysis store.
 *
 * It is three primitive subscriptions plus a boolean off `analysisStore`, and
 * not one of them changes per move — the rail container must never re-render on
 * an opponent move (UX spec §6a), and its children are zero-prop memo leaves, so
 * even when it does they do not.
 */
import {useEffect} from 'react';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {RailState, selectPhase} from '@/Store/liveGame/selectors';

import {resetAssist} from '../assist/assistStore';
import {useAnalysis} from '../analysis/analysisStore';
import {useV2Ui} from '../../theme/useV2Ui';

/**
 * The states that keep the LIVE layout: tabs + moves + nav + actions.
 * `result` is one of them on purpose — the result is an overlay on the board
 * column (`overlay/ResultOverlay`), not a Rail body, so the notation stays on
 * screen and browsable after the game ends. The action bar's game actions
 * disable themselves from `selectGameOver`.
 *
 * M4 added 'watch': a spectated (or replayed) game is the SAME rail — the
 * notation of the watched game, the round chip, and an action bar whose only
 * game action is "Stop watching" (each leaf picks its own source from
 * `selectBoardSource`).
 *
 * Fix G, 2026-10-06 added 'tournament': the idle tournament view (waiting for
 * a pairing, no board of mine up) is now the ordinary rail too — Details /
 * Standings / Pairings / Leaderboard tabs, a 4th action tile, and
 * `stage/TournamentIdle` over the dimmed start position — instead of the old
 * full-width page. `tabsFor` (`rail/tabs/tabSpec.ts`) is what drops Moves/Info
 * while there is no game on the board.
 */
export const LIVE_LAYOUT_STATES: RailState[] = [
  'live',
  'result',
  'evalPending',
  'watch',
  'tournament',
];

/** which body a rail state mounts. 'none' = the state draws a notice only. */
export type RailBody = 'live' | 'matched' | 'none';

export const bodyFor = (railState: RailState): RailBody => {
  if (LIVE_LAYOUT_STATES.includes(railState)) {
    return 'live';
  }
  return railState === 'matched' ? 'matched' : 'none';
};

/**
 * Casual game or tournament game. A STRING, so the tab strip and the panels can
 * each read it without a tournament write re-rendering a casual rail.
 *
 * A WATCH counts as tournament mode: every watch on this lane is started from
 * the tournament tabs (`rail/tournament/panels/**`), so the panels are the way
 * back — and `tournament.id` is still set while it is up.
 *
 * Fix G, 2026-10-06: 'tournament' whenever `tournament.id` is set AND the board
 * is not showing a NON-tournament game of mine — i.e. the current game (if any)
 * IS the tournament/arena family (`family`), OR there is a watch up, OR there is
 * no game of mine on the board at all (`phase === 'none'`, the idle waiting-for-
 * pairing view that `resetForNewGame`/`leaveTournament` land on). Before this
 * fix the function returned 'casual' the moment nothing of mine was playing,
 * which is why the idle tournament view only ever showed Moves/Info — there was
 * no game to make `family` true and nothing else carried the idle case.
 */
export type RailMode = 'casual' | 'tournament';

export const selectRailMode = (s: LiveGameState): RailMode => {
  if (!s.tournament.id) {
    return 'casual';
  }
  const family =
    s.game.gamePlayMode === 'tournament' || s.game.gamePlayMode === 'arena';
  const watching = s.watch.active || !!s.watch.replayGameId;
  const noOwnGame =
    s.game.phase === 'none' || (s.game.phase === 'ended' && family);
  return family || watching || noOwnGame ? 'tournament' : 'casual';
};

export type RailModel = {
  body: RailBody;
  railMode: RailMode;
  /**
   * The engine exists: `rail/Rail/View.tsx` mounts `EngineHost` only while
   * this is true. Fix F-D / D3: `mode !== 'off'` — true for BOTH self-analysis
   * and an assisted game's live eval bar, since the worker's lifecycle is the
   * same mount/unmount either way. Fix K: ALSO never during the idle
   * tournament wait (§9) — `mode` can still read 'analysis' there if a live
   * watch allowed it and then ended (`rail/actions/tiles/
   * AnalysisAction.model.ts`'s own docblock), and there is no board behind
   * the worker to justify it, same guard `rail/surface.ts`'s `showEvalBar`
   * names for `stage/EvalBar`.
   */
  analysisOn: boolean;
  /** the phone keeps M1b's bottom toolbar instead of the compact action bar */
  phone: boolean;
  /**
   * Fix K: Draw/Resign/Abort/Berserk (or Stop watching / Rematch) and the
   * pinned nav row — `rail/surface.ts`'s `showActionBar` / `showMoveNav`,
   * read here as the thing `LIVE_LAYOUT_STATES` alone no longer decides for
   * them: 'tournament' keeps the board layout (§9) but, while idle, has no
   * game of mine (or a watch) to act on. Both are PURE functions of
   * `railState`, already a prop, so neither costs a subscription —
   * `__tests__/surfaceMatrix.test.tsx` proves the two stay equal to
   * `surface.ts`'s own predicates across all 8 states.
   */
  showActionBar: boolean;
  showMoveNav: boolean;
};

export function useRailModel(railState: RailState): RailModel {
  const railMode = useLiveGame(selectRailMode);
  const phase = useLiveGame(selectPhase);
  const analysisMode = useAnalysis(s => s.mode);
  const phone = useV2Ui(s => s.viewport === 'phone');

  const tournamentIdle = railState === 'tournament';
  const liveControls = bodyFor(railState) === 'live' && !tournamentIdle;
  const analysisOn = analysisMode !== 'off' && !tournamentIdle;

  /**
   * §T4 / R1: the wide rail is tournament mode OR self-ANALYSIS. Fix F-D / D3:
   * deliberately `mode === 'analysis'`, not `analysisOn` — an assisted game's
   * live eval bar must look exactly like an ordinary live game, so `mode ===
   * 'assist'` never widens the rail (the branch UI / `EngineLines` it would
   * otherwise make room for are themselves gated off the same way, see
   * `rail/live/LiveBody`). One write, and `useV2Ui.setRailMode` re-derives the
   * rail width and the board size together so the board never ends up sized
   * for the other mode.
   */
  const wide = railMode === 'tournament' || analysisMode === 'analysis';
  useEffect(() => {
    useV2Ui.getState().setRailMode(wide);
  }, [wide]);

  /**
   * A NEW GAME lands on Moves and kills the engine (§T5 + R4's fair-play rule)
   * AND forgets any assistant hints from the game before it (fix F-D / D2 —
   * the same "a new game spawned" guard `resetAnalysis` uses, rather than a
   * second tracker in `hooks/useAssistEffects.ts`). `phase === 'starting'` is
   * `start_game_response`, i.e. the one moment a board of mine appears — a
   * result that is merely dismissed must NOT reset the tab, which is why this
   * watches the phase and not the rail state.
   */
  useEffect(() => {
    if (phase === 'starting') {
      useV2Ui.getState().setRailTab('moves');
      useAnalysis.getState().reset();
      resetAssist();
    }
  }, [phase]);

  /**
   * The rail UNMOUNTS whenever the surface stops being a board (the lobby, a
   * seek, the tournament page — `Body`'s `PAGE_STATES`). Both pieces of state
   * this unit raises outlive it, so both come down here: an engine left running
   * with no board, and a WIDE rail with nothing wide in it on the next game.
   */
  useEffect(
    () => () => {
      useAnalysis.getState().reset();
      useV2Ui.getState().setRailMode(false);
    },
    [],
  );

  return {
    body: bodyFor(railState),
    railMode,
    analysisOn,
    phone,
    showActionBar: liveControls,
    showMoveNav: liveControls,
  };
}

export default useRailModel;
